import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { FormControl, FormLabel, Grid, TextField } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import Palette from '../../../ui-component/ThemePalette';
import { patchApi } from 'views/services/api';
import { hotelSchema } from 'schema';  // Make sure this schema is valid and imported correctly
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PropTypes from 'prop-types';
import { parse } from 'date-fns';

const EditHotel = (props) => {
  const { open, handleClose, data } = props;

  const [initialValues, setInitialValues] = useState({
    email: data?.email || '',
    password: data?.password || '',
    name: data?.name || '',
    contact: data?.contact || '',
    address: data?.address || '',
    gstNumber: data?.gstNumber || '',
    foodgstpercentage: data?.foodgstpercentage || '',
    roomgstpercentage: data?.roomgstpercentage || '',
    mapurl: data?.mapurl || '',
    websiteURL: data?.websiteURL || '',
    googleReviewURL: data?.googleReviewURL || '',
    currencyCode: data?.currencyCode || 'INR',
    checkInTime: data?.checkInTime ? parse(data.checkInTime, 'hh:mm aa', new Date()) : null,
    checkOutTime: data?.checkOutTime ? parse(data.checkOutTime, 'hh:mm aa', new Date()) : null,
  });

  useEffect(() => {
    setInitialValues({
      email: data?.email || '',
      password: data?.password || '',
      name: data?.name || '',
      contact: data?.contact || '',
      address: data?.address || '',
      gstNumber: data?.gstNumber || '',
      foodgstpercentage: data?.foodgstpercentage || '',
      roomgstpercentage: data?.roomgstpercentage || '',
      mapurl: data?.mapurl || '',
      websiteURL: data?.websiteURL || '',
      googleReviewURL: data?.googleReviewURL || '',
      currencyCode: data?.currencyCode || 'INR',
      checkInTime: data?.checkInTime ? parse(data.checkInTime, 'hh:mm aa', new Date()) : null,
      checkOutTime: data?.checkOutTime ? parse(data.checkOutTime, 'hh:mm aa', new Date()) : null,
    });
  }, [data]);

  const formik = useFormik({
    initialValues,
    validationSchema: hotelSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true, // ensures validation runs as the user types
    onSubmit: async (values, { resetForm }) => {
      console.log('🚀 FORM SUBMIT TRIGGERED');
      console.log('Form Data on Submit ===>', values);

      // Prepare time fields for submission
      const formattedCheckInTime = values.checkInTime ? values.checkInTime.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
      }) : '';
      const formattedCheckOutTime = values.checkOutTime ? values.checkOutTime.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
      }) : '';

      values.checkInTime = formattedCheckInTime;
      values.checkOutTime = formattedCheckOutTime;

      // Log the formatted values
      console.log('Formatted values for submission ===>', values);

      // Call updateAdmin function
      await updateAdmin(values, resetForm);
    }
  });

  const updateAdmin = async (values, resetForm) => {
    console.log('🔄 updateAdmin function called with values:', values);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === 'hotelImage' && values.hotelImage) {
          formData.append('hotelImage', values.hotelImage);
        } else {
          formData.append(key, values[key]);
        }
      });

      // Show the data before API call (for debugging)
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1]}`);
      }

      const response = await patchApi(`api/hotel/edit/${data?._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status === 200) {
        toast.success('Hotel updated successfully');
        resetForm();
        handleClose();
        // Instead of full page reload, trigger a refresh of the parent component
        if (props.onUpdate) {
          props.onUpdate();
        }
      } else {
        toast.error('Unable to update hotel');
      }
    } catch (error) {
      console.log('Error:', error);
      toast.error('Unable to update hotel');
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue('hotelImage', file);
    }
  };

  return (
    <div>
      <Dialog open={open} onClose={handleClose} aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description">
        <DialogTitle
          id="scroll-dialog-title"
          style={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h6">Edit Hotel Profile</Typography>
          <Typography>
            <ClearIcon onClick={handleClose} style={{ cursor: 'pointer' }} />
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ width: '100%', minWidth: '600px' }}>
          <form onSubmit={formik.handleSubmit}>
            <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
              {/* Email */}
              <Grid item xs={12} sm={12} md={6} sx={{ marginTop: '10px' }}>
                <FormLabel>Email</FormLabel>
                <TextField
                  id="email"
                  name="email"
                  size="small"
                  fullWidth
                  disabled
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>

              {/* Name */}
              <Grid item xs={12} sm={12} md={6} sx={{ marginTop: '10px' }}>
                <FormLabel>Name</FormLabel>
                <TextField
                  id="name"
                  name="name"
                  size="small"
                  fullWidth
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={12} md={12} sx={{ marginTop: '10px' }}>
                <Grid item xs={6} sm={6} md={6} sx={{ marginTop: '10px' }}>
                  <FormLabel>Phone Number</FormLabel>
                  <TextField
                    id="contact"
                    name="contact"
                    label=""
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.contact}
                    onChange={formik.handleChange}
                    error={formik.touched.contact && Boolean(formik.errors.contact)}
                    helperText={formik.touched.contact && formik.errors.contact}
                  />
                </Grid>
              </Grid>

              {/* Address */}
              <Grid item xs={12} sm={12} md={12} sx={{ marginTop: '10px' }}>
                <FormLabel>Address</FormLabel>
                <TextField
                  id="address"
                  name="address"
                  label=""
                  type="text"
                  size="small"
                  fullWidth
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>

              {/* Food GST Percentage */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>GST % on Food</FormLabel>
                  <TextField
                    id="foodgstpercentage"
                    name="foodgstpercentage"
                    placeholder="in %"
                    label=""
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.foodgstpercentage}
                    onChange={formik.handleChange}
                    error={formik.touched.foodgstpercentage && Boolean(formik.errors.foodgstpercentage)}
                    helperText={formik.touched.foodgstpercentage && formik.errors.foodgstpercentage}
                  />
                </FormControl>
              </Grid>

              {/* Room GST Percentage */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>GST % on Room</FormLabel>
                  <TextField
                    id="roomgstpercentage"
                    name="roomgstpercentage"
                    placeholder="in %"
                    label=""
                    type="number"
                    size="small"
                    fullWidth
                    value={formik.values.roomgstpercentage}
                    onChange={formik.handleChange}
                    error={formik.touched.roomgstpercentage && Boolean(formik.errors.roomgstpercentage)}
                    helperText={formik.touched.roomgstpercentage && formik.errors.roomgstpercentage}
                  />
                </FormControl>
              </Grid>

              {/* Currency Code */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>Currency Code</FormLabel>
                  <TextField
                    id="currencyCode"
                    name="currencyCode"
                    placeholder="e.g., INR, USD"
                    label=""
                    type="text"
                    size="small"
                    fullWidth
                    value={formik.values.currencyCode}
                    onChange={formik.handleChange}
                    error={formik.touched.currencyCode && Boolean(formik.errors.currencyCode)}
                    helperText={formik.touched.currencyCode && formik.errors.currencyCode}
                  />
                </FormControl>
              </Grid>

              {/* Google Review URL` */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>Google Review URL</FormLabel>
                  <TextField
                    id="googleReviewURL"
                    name="googleReviewURL"
                    placeholder="e.g., https://www.google.com/"
                    label=""
                    type="text"
                    size="small"
                    fullWidth
                    value={formik.values.googleReviewURL}
                    onChange={formik.handleChange}
                    error={formik.touched.googleReviewURL && Boolean(formik.errors.googleReviewURL)}
                    helperText={formik.touched.googleReviewURL && formik.errors.googleReviewURL}
                  />
                </FormControl>
              </Grid>


              {/* Google Map URL` */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>Google Map URL</FormLabel>
                  <TextField
                    id="googleMapURL"
                    name="googleMapURL"
                    placeholder="e.g., https://www.google.com/maps/"
                    label=""
                    type="text"
                    size="small"
                    fullWidth
                    value={formik.values.googleMapURL}
                    onChange={formik.handleChange}
                    error={formik.touched.googleMapURL && Boolean(formik.errors.googleMapURL)}
                    helperText={formik.touched.googleMapURL && formik.errors.googleMapURL}
                  />
                </FormControl>
              </Grid>

              {/* GST Number */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>GST Number</FormLabel>
                  <TextField
                    id="gstNumber"
                    name="gstNumber"
                    placeholder="e.g., FHPRT7453P"
                    label=""
                    type="text"
                    size="small"
                    fullWidth
                    value={formik.values.gstNumber}
                    onChange={formik.handleChange}
                    error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                    helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                  />
                </FormControl>
              </Grid>
              {/* Website URL` */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={{ marginTop: '10px' }}>
                  <FormLabel>Website URL</FormLabel>
                  <TextField
                    id="websiteURL"
                    name="websiteURL"
                    placeholder="e.g., https://www.google.com/"
                    label=""
                    type="text"
                    size="small"
                    fullWidth
                    value={formik.values.websiteURL}
                    onChange={formik.handleChange}
                    error={formik.touched.websiteURL && Boolean(formik.errors.websiteURL)}
                    helperText={formik.touched.websiteURL && formik.errors.websiteURL}
                  />
                </FormControl>
              </Grid>
              {/* Time Pickers */}
              <Grid container spacing={2}>
                {/* Check-In Time */}
                <Grid item xs={12} sm={6}>
                  <FormLabel>Check-In Time</FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      id="checkInTime"
                      name="checkInTime"
                      value={formik.values.checkInTime}
                      onChange={(value) => formik.setFieldValue('checkInTime', value)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Check-Out Time */}
                <Grid item xs={12} sm={6}>
                  <FormLabel>Check-Out Time</FormLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      id="checkOutTime"
                      name="checkOutTime"
                      value={formik.values.checkOutTime}
                      onChange={(value) => formik.setFieldValue('checkOutTime', value)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>

              {/* Hotel Image */}
              <Grid item xs={12} sm={12} md={12} sx={{ marginTop: '10px' }}>
                <FormLabel>Select Hotel Image</FormLabel>
                <input
                  id="hotelImage"
                  name="hotelImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'block' }}
                />
                {formik.touched.hotelImage && formik.errors.hotelImage && (
                  <div style={{ color: 'red' }}>{formik.errors.hotelImage}</div>
                )}
              </Grid>
            </DialogContentText>
            <DialogActions>
              <Button
                type="submit"
                variant="contained"
                style={{ backgroundColor: Palette.info, color: '#fff' }}
                onClick={() => {
                  console.log('💾 Save Changes button clicked');
                  console.log('Form is valid:', formik.isValid);
                  console.log('Form errors:', formik.errors);
                  console.log('Form touched:', formik.touched);
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

EditHotel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

export default EditHotel;
